'use client'

/**
 * Suggestion Box form (/suggestion-box). Posts { title, description } to
 * POST /api/suggestions, which stores it in `roadmap_ideas` for the staff
 * Customer Voice queue (/admin/ideas). Rendered only for signed-in visitors —
 * the page shows a sign-in prompt instead when there's no session.
 *
 * Adapted from the old roadmap "Submit Idea" modal (removed with /roadmap in
 * #1293) into an inline page form.
 *
 * Bot protection (checked server-side in the route):
 *  - Cloudflare Turnstile widget (shared with the auth forms; renders nothing
 *    without NEXT_PUBLIC_TURNSTILE_SITE_KEY). Its single-use token is sent as
 *    `captchaToken` and the widget is reset after every attempt. Send is never
 *    disabled for a missing token (same as the auth forms): while there is none
 *    a hint asks the member to complete the check, and the server decides. If
 *    the widget can't load (content blocker, network filter, Turnstile error)
 *    the form says so and points to a support ticket instead of waiting for a
 *    token that will never arrive.
 *  - A honeypot input (SUGGESTION_HONEYPOT_FIELD, a non-semantic name so
 *    password managers leave it alone) hidden from people and assistive tech
 *    with a clip-based visually-hidden style (no off-screen offset, which would
 *    add a huge horizontal scroll in the right-to-left Arabic layout). Read
 *    from the DOM at submit, so a bot that fills it programmatically is caught
 *    too.
 *  - `elapsedMs` since the form rendered (performance.now(), so no clock skew).
 *    If the member is quicker than SUGGESTION_MIN_FILL_MS (e.g. re-sending a
 *    restored draft), we wait out the remainder rather than get dropped.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { CheckCircle2 } from 'lucide-react'
import { Link } from '@/i18n/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CAPTCHA_ENABLED, Turnstile } from '@/components/auth/turnstile'
import {
  SUGGESTION_DESCRIPTION_MAX,
  SUGGESTION_HONEYPOT_FIELD,
  SUGGESTION_MIN_FILL_MS,
  SUGGESTION_TITLE_MAX,
} from '@/lib/suggestions'

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error'

/**
 * Unsent draft kept across a sign-in round trip: when the session has expired
 * (401) the form saves what the member typed here before they follow the
 * sign-in link, and restores it when they come back. Per-tab convenience
 * only — every access is guarded (storage can be blocked or throw).
 */
const DRAFT_KEY = 'suggestion-box:draft'
const SIGN_IN_HREF = `/auth?next=${encodeURIComponent('/suggestion-box')}`
/** Margin over the server's minimum, so timer granularity can't trip it. */
const FILL_TIME_MARGIN_MS = 250

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

function saveDraft(draft: { title: string; description: string }) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Storage unavailable — the member just retypes.
  }
}

function takeDraft(): { title: string; description: string } | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    sessionStorage.removeItem(DRAFT_KEY)
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { title, description } = parsed as { title?: unknown; description?: unknown }
    return {
      title: typeof title === 'string' ? title.slice(0, SUGGESTION_TITLE_MAX) : '',
      description:
        typeof description === 'string' ? description.slice(0, SUGGESTION_DESCRIPTION_MAX) : '',
    }
  } catch {
    return null
  }
}

export function SuggestionForm() {
  const t = useTranslations('suggestionBoxPage')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  // The last failure was a 401 (session expired) → offer a sign-in link.
  const [signedOut, setSignedOut] = useState(false)
  const successRef = useRef<HTMLHeadingElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const returnFocusToForm = useRef(false)
  const honeypotRef = useRef<HTMLInputElement>(null)
  // performance.now() when the form became fillable (mount / "send another").
  const renderedAt = useRef<number | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | undefined>()
  const [captchaReset, setCaptchaReset] = useState(0)
  // The widget reported it can't produce a token (script blocked / error).
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false)
  const awaitingCaptcha = CAPTCHA_ENABLED && !captchaToken && !captchaUnavailable

  useEffect(() => {
    renderedAt.current = performance.now()
  }, [])

  // Restore a draft saved before a sign-in round trip (see DRAFT_KEY).
  useEffect(() => {
    const draft = takeDraft()
    if (!draft) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot restore from sessionStorage after mount (not available during SSR)
    setTitle(draft.title)
    setDescription(draft.description)
  }, [])

  // Move focus to the confirmation so screen-reader users hear it, and back to
  // the title field when they choose to send another.
  useEffect(() => {
    if (status === 'success') successRef.current?.focus()
    if (status === 'idle' && returnFocusToForm.current) {
      returnFocusToForm.current = false
      titleRef.current?.focus()
    }
  }, [status])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const trimmedTitle = title.trim()
    const trimmedDescription = description.trim()
    if (!trimmedTitle || !trimmedDescription) {
      setStatus('error')
      setError(t('errorRequired'))
      return
    }

    setStatus('loading')
    setError(null)
    setSignedOut(false)

    // Never trip the server's minimum-fill-time check as a real person.
    const startedAt = renderedAt.current ?? performance.now()
    const wait = SUGGESTION_MIN_FILL_MS + FILL_TIME_MARGIN_MS - (performance.now() - startedAt)
    if (wait > 0) await sleep(wait)
    const elapsedMs = Math.round(performance.now() - startedAt)

    const token = captchaToken
    // Turnstile tokens are single-use — request a fresh one for any retry.
    setCaptchaToken(undefined)
    setCaptchaReset((n) => n + 1)

    try {
      const res = await fetch('/api/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: trimmedTitle,
          description: trimmedDescription,
          captchaToken: token,
          [SUGGESTION_HONEYPOT_FIELD]: honeypotRef.current?.value ?? '',
          elapsedMs,
        }),
      })
      if (!res.ok) {
        setStatus('error')
        if (res.status === 401) {
          // Keep what they typed so it survives the sign-in round trip.
          saveDraft({ title, description })
          setSignedOut(true)
        }
        const code =
          res.status === 400
            ? ((await res.json().catch(() => null)) as { code?: unknown } | null)?.code
            : undefined
        setError(
          res.status === 401
            ? t('errorSignedOut')
            : res.status === 429
              ? t('errorRateLimited')
              : code === 'captcha_failed'
                ? captchaUnavailable
                  ? t('captchaUnavailable')
                  : t('errorCaptcha')
                : t('errorMessage'),
        )
        return
      }
      setTitle('')
      setDescription('')
      setStatus('success')
    } catch {
      setStatus('error')
      setError(t('errorMessage'))
    }
  }

  if (status === 'success') {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-4 pt-6" role="status">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
            <h2
              ref={successRef}
              tabIndex={-1}
              className="text-lg font-semibold text-gray-900 outline-none dark:text-white"
            >
              {t('successTitle')}
            </h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{t('successMessage')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              returnFocusToForm.current = true
              renderedAt.current = performance.now()
              setStatus('idle')
            }}
          >
            {t('sendAnother')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t('formHeading')}</h2>
        </CardTitle>
        <CardDescription>{t('formDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="relative space-y-5" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="suggestion-title">{t('titleLabel')}</Label>
            <Input
              ref={titleRef}
              id="suggestion-title"
              name="title"
              required
              aria-required="true"
              maxLength={SUGGESTION_TITLE_MAX}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('titlePlaceholder')}
              aria-describedby="suggestion-title-count"
            />
            <p id="suggestion-title-count" className="text-end text-xs text-gray-500 dark:text-gray-400">
              {t('characterCount', { count: title.length, max: SUGGESTION_TITLE_MAX })}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="suggestion-description">{t('descriptionLabel')}</Label>
            <textarea
              id="suggestion-description"
              name="description"
              required
              aria-required="true"
              rows={7}
              maxLength={SUGGESTION_DESCRIPTION_MAX}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('descriptionPlaceholder')}
              aria-describedby="suggestion-description-count"
              className="w-full border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            <p
              id="suggestion-description-count"
              className="text-end text-xs text-gray-500 dark:text-gray-400"
            >
              {t('characterCount', { count: description.length, max: SUGGESTION_DESCRIPTION_MAX })}
            </p>
          </div>

          {/* Honeypot: hidden from people and screen readers, skipped by Tab.
              Must stay empty (see SUGGESTION_HONEYPOT_FIELD). Clipped to 1px
              (sr-only) rather than display:none, which some bots check for, and
              not pushed off-screen, which scrolls sideways in RTL. The data-*
              attributes ask 1Password / LastPass / Bitwarden / Dashlane not to
              autofill it. */}
          <div aria-hidden="true" className="sr-only">
            <label htmlFor="suggestion-extra">{t('honeypotLabel')}</label>
            <input
              ref={honeypotRef}
              id="suggestion-extra"
              type="text"
              name={SUGGESTION_HONEYPOT_FIELD}
              tabIndex={-1}
              autoComplete="off"
              data-1p-ignore=""
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
              defaultValue=""
            />
          </div>

          <Turnstile
            onVerify={(token) => {
              setCaptchaUnavailable(false)
              setCaptchaToken(token)
            }}
            onExpire={() => setCaptchaToken(undefined)}
            onError={() => setCaptchaUnavailable(true)}
            resetSignal={captchaReset}
            action="suggestion_box"
          />

          {/* Shown until an error replaces it (the error block below then
              carries the same support link). */}
          {CAPTCHA_ENABLED && captchaUnavailable && status !== 'error' && (
            <div role="alert" className="space-y-2">
              <p className="text-sm text-red-600 dark:text-red-400">{t('captchaUnavailable')}</p>
              <Link
                href="/account?tab=support"
                className="inline-block text-sm font-medium text-brand-700 underline hover:no-underline dark:text-brand-400"
              >
                {t('supportLink')}
              </Link>
            </div>
          )}

          {status === 'error' && error && (
            <div role="alert" className="space-y-2">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              {signedOut && (
                <Link
                  href={SIGN_IN_HREF}
                  className="inline-block text-sm font-medium text-brand-700 underline hover:no-underline dark:text-brand-400"
                >
                  {t('signInButton')}
                </Link>
              )}
              {!signedOut && CAPTCHA_ENABLED && captchaUnavailable && (
                <Link
                  href="/account?tab=support"
                  className="inline-block text-sm font-medium text-brand-700 underline hover:no-underline dark:text-brand-400"
                >
                  {t('supportLink')}
                </Link>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Button
              type="submit"
              disabled={status === 'loading'}
              aria-describedby={awaitingCaptcha ? 'suggestion-captcha-hint' : undefined}
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              {status === 'loading' ? t('submitting') : t('submitButton')}
            </Button>
            {awaitingCaptcha && status !== 'loading' && (
              <p id="suggestion-captcha-hint" className="text-sm text-gray-600 dark:text-gray-400">
                {t('captchaHint')}
              </p>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
